/**
 * Watch Frontline (GAME_DESIGN Abschnitt 13).
 *
 * Der Auge-Knopf zeigte bis zum 09.10.2026 die Lane des Gegners. Auf einer
 * gemeinsamen Lane gibt es die nicht mehr - beide Armeen stehen auf demselben
 * Feld. Stattdessen springt die Kamera zur **aktuellen Kampflinie**, und der
 * eigene Hero bleibt, wo er ist.
 *
 * Diese Datei beantwortet zwei Fragen, beide ohne laufendes Match pruefbar:
 *
 *   1. Wohin schaut die Kamera?
 *   2. Steht die eigene Linie unter Druck?
 *
 * Gerechnet wird in einem gemeinsamen Mass: dem Anteil der Lane, von BLAUS
 * Anchor aus gezaehlt. 0 ist Blaus Anchor, 1 das von Orange. Beide Spieler
 * benutzen dieselbe Zahl - sonst muesste jede Stelle wissen, aus wessen Sicht
 * sie gerade liest.
 */

import { FRONT_PRESSURE_WARNING_MS, FRONTLINE_PRESSURE_SHARE } from '@dueline/game-data';
import type { PlayerSide } from '@dueline/shared';

/** Die Frontline eines Spielers auf der gemeinsamen Achse. */
export interface FrontAnteil {
  /** 0 bis 1, von Blaus Anchor aus. */
  anteil: number;
  /** Haelt dieser Spieler ueberhaupt Raum? */
  aktiv: boolean;
}

/**
 * Wohin die Kamera bei Watch Frontline schaut, als Anteil der Lane.
 *
 * **Produktentscheidung vom 09.10.2026: auf die EIGENE Frontline.** Also
 * dorthin, wo die eigenen Creeps gerade am weitesten vorne stehen und
 * kaempfen.
 *
 * Hier stand zuerst die Mitte zwischen beiden Linien. Das war die falsche
 * Frage: Wer den Knopf drueckt, will wissen, wie weit SEINE Armee kommt -
 * nicht, wo ein Mittelwert liegt. Ein Mittelwert zeigt ausserdem bei
 * gleichmaessigem Druck immer dieselbe Stelle, egal wie gut es gerade
 * laeuft.
 *
 * Die eigene Frontline ist bereits gedaempft (ziehFrontlineNach) und von
 * Laeufern bereinigt: Sie zeigt die vorderste GRUPPE, nicht einen einzelnen
 * durchgerutschten Runner. Genau das verlangt das Design.
 *
 * Lebt die eigene Armee gerade nicht, zaehlt die Linie des Gegners - von
 * dort kommt der Angriff, und der Knopf soll immer etwas zeigen. Haelt
 * niemand Raum, bleibt die Mitte der Lane.
 */
export function kameraAnteil(eigene: FrontAnteil, gegnerische: FrontAnteil): number {
  if (eigene.aktiv) return eigene.anteil;
  if (gegnerische.aktiv) return gegnerische.anteil;
  return 0.5;
}

/** Warum die eigene Linie gerade kritisch ist. */
export type DruckGrund = 'front_zurueckgedraengt' | 'anchor_getroffen';

export interface DruckEingabe {
  /** Auf welcher Seite der Spieler zuhause ist. */
  side: PlayerSide;
  /** Die Frontline des GEGNERS - sie sagt, wie weit er vorgedrungen ist. */
  gegnerischeFront: FrontAnteil;
  /** Millisekunden seit dem letzten Treffer am eigenen Anchor. null = nie. */
  msSeitAnchorTreffer: number | null;
  /** Wie lange ein Treffer nachwirkt. */
  trefferFensterMs: number;
  /**
   * Ab welchem Vordringen des Gegners die Linie als gedrueckt gilt.
   *
   * Fehlt sie, gilt FRONTLINE_PRESSURE_SHARE - das ist der Wert fuer den
   * Menschen. Der Testgegner benutzt dieselbe Regel mit einer eigenen
   * Schwelle: Ein schwacher Bot soll den Druck spaeter bemerken
   * (Produktentscheidung vom 09.10.2026).
   */
  schwelle?: number;
}

/**
 * Steht die eigene Linie unter Druck (Produktentscheidung vom 07.10.2026)?
 *
 * Zwei Bedingungen, beide an der Frontline festgemacht:
 *
 *   - Die Front steht zu weit auf der eigenen Seite, oder
 *   - das eigene Anchor wurde gerade getroffen.
 *
 * Die alte Fassung haengte an drei Dingen, die alle auf die eigene GETRENNTE
 * Lane zielten: Creeps kurz vor dem Portal, ein angeschlagener Hero, ein
 * Portaltreffer. Die ersten beiden sagen auf der gemeinsamen Lane nichts mehr
 * ueber die Lage - ein angeschlagener Hero ist ein eigenes Problem, keine
 * Frontmeldung.
 *
 * Ein durchgelaufener Laeufer loest die Warnung ueber die zweite Bedingung mit
 * aus, auch wenn die Front gut steht. Das ist bewusst so: Ein Treffer am
 * eigenen Anchor ist immer eine Meldung wert.
 */
export function druckGruende(eingabe: DruckEingabe): DruckGrund[] {
  const gruende: DruckGrund[] = [];

  if (eingabe.gegnerischeFront.aktiv) {
    /*
     * "Zu weit auf der eigenen Seite" - gemessen daran, wie viel Weg der
     * Gegner schon hinter sich hat. Aus Sicht von Blau liegt sein eigenes
     * Anchor bei 0, die Front des Gegners waechst also nach unten.
     */
    const vorgedrungen =
      eingabe.side === 'blue'
        ? 1 - eingabe.gegnerischeFront.anteil
        : eingabe.gegnerischeFront.anteil;
    const schwelle = eingabe.schwelle ?? FRONTLINE_PRESSURE_SHARE;
    if (vorgedrungen >= schwelle) gruende.push('front_zurueckgedraengt');
  }

  const seitTreffer = eingabe.msSeitAnchorTreffer;
  if (seitTreffer !== null && seitTreffer <= eingabe.trefferFensterMs) {
    gruende.push('anchor_getroffen');
  }

  return gruende;
}

/**
 * Was sich die Anzeige von einem Takt zum naechsten merken muss.
 *
 * Zwei Zahlen, und beide werden gebraucht: WANN die laufende Warnung
 * begonnen hat, und OB im letzten Takt ueberhaupt Druck war. Ohne die
 * zweite gaebe es keine Flanke - die Warnung wuesste nicht, ob der Druck
 * gerade erst anfaengt oder schon eine Weile anhaelt.
 */
export interface DruckWarnung {
  /** Beginn der laufenden Warnung in Millisekunden. null = keine laeuft. */
  seitMs: number | null;
  /** Stand die Linie im letzten Takt unter Druck? */
  warUnterDruck: boolean;
}

/** Der Zustand vor dem ersten Takt. */
export const KEINE_DRUCKWARNUNG: DruckWarnung = { seitMs: null, warUnterDruck: false };

/**
 * Soll die Druckwarnung JETZT zu sehen sein
 * (Produktentscheidung vom 12.10.2026)?
 *
 * Sie blitzt auf: FRONT_PRESSURE_WARNING_MS lang, dann ist sie weg - auch
 * wenn die Linie weiter unter Druck steht. Robins Rueckmeldung aus dem
 * Spiel: Sie soll nicht durchgehend angezeigt werden, nur weil die Lage
 * noch kritisch ist.
 *
 * Wieder kommen darf sie erst, wenn der Druck ZWISCHENDURCH weg war. Damit
 * meldet sie ein Ereignis ("jetzt wird es eng") statt eines Zustands - und
 * dass sie wieder auftaucht, heisst dann wirklich etwas Neues.
 *
 * Ein Wechsel des GRUNDES loest sie ausdruecklich NICHT neu aus: Wer
 * zurueckgedraengt wird und dabei auch noch einen Treffer am Anchor
 * kassiert, soll nicht zweimal dieselbe Meldung sehen. Erst Ruhe, dann
 * wieder Druck.
 *
 * Was die Warnung NICHT beruehrt: druckGruende selbst. Der Testgegner
 * benutzt dieselbe Regel, um zu reagieren, und der soll auf anhaltenden
 * Druck anhaltend reagieren. Hier geht es nur um die Anzeige.
 */
export function stepDruckWarnung(eingabe: {
  /** Steht die Linie gerade unter Druck (druckGruende nicht leer)? */
  unterDruck: boolean;
  /** Die aktuelle Zeit in Millisekunden. */
  jetztMs: number;
  /** Was sich die Anzeige gemerkt hat. */
  zustand: DruckWarnung;
}): { sichtbar: boolean; zustand: DruckWarnung } {
  // Kein Druck: Alles zurueck auf Anfang - und damit wieder scharf.
  if (!eingabe.unterDruck) {
    return { sichtbar: false, zustand: KEINE_DRUCKWARNUNG };
  }

  // Die Flanke: Druck, wo eben noch keiner war. Jetzt und nur jetzt startet
  // die Warnung.
  if (!eingabe.zustand.warUnterDruck || eingabe.zustand.seitMs === null) {
    return {
      sichtbar: true,
      zustand: { seitMs: eingabe.jetztMs, warUnterDruck: true },
    };
  }

  const gelaufen = eingabe.jetztMs - eingabe.zustand.seitMs;
  return {
    sichtbar: gelaufen < FRONT_PRESSURE_WARNING_MS,
    zustand: eingabe.zustand,
  };
}
